import { SearchBar, SearchBarProps, styled } from '@zeiss/beyond-online-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-responsive-s);
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-2xl);

  @media (width >= 768px) {
    flex-direction: row;
  }
`;

const ActionContainer = styled.div`
  display: flex;
  flex-shrink: 0; /* Prevent width of search container from shrinking the action contents */
  flex-direction: column;
  gap: var(--spacing-m);
  width: 100%;

  @media (width >= 768px) {
    flex-direction: row;
    width: auto;
  }
`;

const SearchContainer = styled.div`
  width: 100%;

  @media (width >= 768px) {
    max-width: 575px;
  }
`;

/**
 * Beyond workaround: On mobile the current (v3.17.0) implementation
 * constrains the width to a hard-coded 312px.
 */
const StyledSearchBar = styled(SearchBar)`
  width: 100%;
`;

interface ActionBarProps {
  children: React.ReactNode;
  onSearch: SearchBarProps['onSearch'];
  /**
   * Optional description of searchable fields to display in the search placeholder.
   *
   * Defaults to translated `t('common:name')` if not provided.
   *
   * **Important**: Pass already translated text, not raw text.
   *
   * @example
   * ```tsx
   * // Correct - translated text
   * <ActionBar searchableFields={`${t('common:name')}, ${t('common:email')}, ${t('common:phone')}`} />
   * ```
   *
   * ```tsx
   * // Incorrect - raw text
   * <ActionBar searchableFields="name, email, phone" />
   * ```
   */
  searchableFields?: string;
}

export const ActionBar = ({
  children,
  onSearch,
  searchableFields,
}: ActionBarProps) => {
  const { t } = useTranslation(['common']);

  const [searchQuery, setSearchQuery] = useState<string>('');

  const handleChange: SearchBarProps['onChange'] = (
    event,
    { reason, value },
  ) => {
    setSearchQuery(value);

    if (reason === 'clear') {
      onSearch?.(event, { searchValue: value });
    }
  };

  return (
    <Container data-testid="ActionBar">
      <SearchContainer>
        <StyledSearchBar
          data-testid="ActionBarSearch"
          onChange={handleChange}
          onSearch={onSearch}
          placeholder={t('common:input.searchPlaceholder', {
            x: searchableFields ?? `${t('common:name')}`,
          })}
          searchButton={{
            children: t('common:label.search'),
          }}
          value={searchQuery}
        />
      </SearchContainer>
      <ActionContainer>{children}</ActionContainer>
    </Container>
  );
};
